import { useEffect, useState } from "react"
import { HashLink } from "react-router-hash-link"
import logo from "../../assets/logo.png"
import burgermenu from "../../assets/burgermenu.svg"
import { ReactComponent as CLOSE } from "../../assets/close.svg"
import Nav3DAnimation from "./Nav3DAnimation"
import { AnimatePresence, motion } from "framer-motion"
import BaseQuote from "../general/BaseQuote"
import BaseOutlineText from "../general/BaseOutlineText"
import { X } from "lucide-react"

interface MousePosition {
    x: number
    y: number
}

export default function Header() {
    const [nav, setNav] = useState(false)
    const [bannerVisible, setBannerVisible] = useState(true)
    const [consultingTransition, setConsultingTransition] = useState(false)

    const [mousePosition, setMousePosition] = useState<MousePosition>({
        x: 0,
        y: 0,
    })

    useEffect(() => {
        if (!consultingTransition) {
            return
        }

        const timeoutId = window.setTimeout(() => {
            window.location.href = "https://wicode.io/beratung"
        }, 650)

        return () => window.clearTimeout(timeoutId)
    }, [consultingTransition])

    const handleNav = () => {
        setNav(!nav)
    }

    const handleConsultingClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()

        if (consultingTransition) {
            return
        }

        setConsultingTransition(true)
    }

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        //     setMousePosition({ x: event.clientX, y: event.clientY });
        const x = (event.clientX / window.innerWidth) * 2 - 1
        const y = -(event.clientY / window.innerHeight) * 2 + 1
        setMousePosition({ x, y })
    }

    return (
        <div className={`fixed inset-x-0 top-0 z-[501] transition-all duration-300`}>
            <AnimatePresence>
                {bannerVisible && (
                    <motion.div
                        initial={{ opacity: 0, y: -16, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -16, height: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 24 }}
                        className="overflow-hidden border-b border-white/10 bg-base-100 text-white">
                        <div className="mx-auto flex max-w-page items-center justify-between gap-4 px-4 py-1.5 sm:px-8 md:px-16 xl:px-0">
                            <a
                                href="https://wicode.io/beratung"
                                onClick={handleConsultingClick}
                                className="group flex items-center gap-3 text-xs font-medium sm:text-sm">
                                <span className="inline-flex h-2 w-2 rounded-full bg-primary transition-transform duration-300 group-hover:scale-125" />
                                <span className="text-base-content/80">
                                Wir beraten Sie auch: Mit Strategie, Design und Technik begleiten wir dich zu deiner passenden Lösung:
                                
                                </span>
                                <a
                                    href="https://wicode.io/beratung"
                                    onClick={handleConsultingClick}
                                    className="flex items-center gap-1 -ml-2 group p-0! text-base-content/80  sm:text-sm underline"
                                >
                                    Zur Beratungs Website

                                </a>
                            </a>
                            <div className="flex items-center gap-2">
                               
                                <button
                                    type="button"
                                    aria-label="Banner schließen"
                                    onClick={() => setBannerVisible(false)}
                                    className="rounded-full p-2 text-white/70 transition-colors duration-300 cursor-pointer hover:bg-white/10 hover:text-white">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className={`max-w-page flex justify-between py-8 px-4 sm:px-8 md:px-16 xl:px-0 mx-auto`}>
                <HashLink to={"/#_"} className="text-xl cursor-pointer text-primary z-[1001]">
                    <img src={logo} alt="logo" className="w-12" />
                </HashLink>
                <button type="button" className="z-[1001]" onClick={handleNav} aria-label={nav ? "Menü schließen" : "Menü öffnen"}>
                    {nav ? <CLOSE className="hover:rotate-90 transition-all duration-300 group stroke-primary max-h-8" /> : <img src={burgermenu} alt="burgermenu" />}
                </button>
            </div>
            {/* Navigation Body */}
            <motion.div
                initial={{
                    y: "100vh",
                }}
                animate={{
                    y: nav ? 0 : "100vh",
                }}
                transition={{
                    duration: 0.6,
                    type: "ease",
                }}
                className="fixed bottom-0 left-0 w-full h-[100dvh] bg-base-100 z-50"
                onMouseMove={handleMouseMove}>
                <div className="max-w-page h-full mx-auto px-4 md:px-16 xl:px-0 flex flex-col pt-32">
                    <div className="flex-grow flex flex-col justify-center text-white text-4xl gap-4">
                        <LinkElement onClick={handleNav} url={"/"} labelPrimary={"Homepage"} labelSecondary={"Landing"} />
                        <LinkElement onClick={handleNav} url={"/about"} labelPrimary={"Über Uns"} labelSecondary={"About Us"} />
                        <LinkElement onClick={handleNav} url={"/portfolio"} labelPrimary={"Portfolio"} labelSecondary={"Projects"} />
                        {/* <LinkElement onClick={handleNav} url={"/blog"} labelPrimary={"Blog"} labelSecondary={"News"} /> */}
                        <LinkElement onClick={handleNav} url={"/jobs"} labelPrimary={"Join Us"} labelSecondary={"Stellen"} />
                        <LinkElement onClick={handleNav} url={"/contact"} labelPrimary={"Kontakt"} labelSecondary={"Say hello"} />
                    </div>
                    <div className="flex items-center justify-center py-8">
                        <BaseQuote
                            textSize="text-md sm:text-xl"
                            textAnimationBlock text={"<strong>Software</strong> that inspires."} />
                    </div>
                </div>
                <div className="hidden lg:block w-1/2 absolute right-0  bottom-0 ">
                    <Nav3DAnimation mousePosition={mousePosition} />
                </div>
            </motion.div>

            <AnimatePresence>
                {consultingTransition && (
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", stiffness: 120, damping: 18, mass: 0.9 }}
                        className="fixed inset-0 z-[2000] bg-[#f8f8f8]"
                        aria-hidden="true"
                    />
                )}
            </AnimatePresence>
        </div>
    )
}

type LinkElementType = {
    onClick: () => void
    url: string
    labelPrimary: string
    labelSecondary: string
}
function LinkElement(props: LinkElementType) {
    const [hover, sethover] = useState<string>()
    return (
        <motion.div
            animate={{
                x: typeof window !== "undefined" && window.innerWidth >= 1024 && hover === props.labelPrimary ? 16 : 0,
            }}
            transition={{
                duration: 0.5,
                type: "ease",
            }}
            onHoverStart={() => sethover(props.labelPrimary)}
            onHoverEnd={() => sethover(undefined)}
            className="my-4">
            <HashLink to={props.url + "#_"} onClick={props.onClick} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl  2xl:text-7xl text-center lg:text-left relative z-40 hover:text-primary group font-semibold  w-full flex justify-center lg:justify-start">
                <p className="font-text z-10 font-bold w-fit">{props.labelPrimary}</p>
                <BaseOutlineText
                    weight={"medium"}
                    text={props.labelSecondary}
                    textSize="text-5xl sm:text-6xl md:text-8xl xl:text-9xl"
                    classProps="absolute flex lg:block  w-full lg:w-fit justify-center lg:left-8 top-1/2 -translate-y-1/2 transition-opacity ease-in duration-75 opacity-25 lg:opacity-0 lg:group-hover:opacity-100  -z-10"
                />
            </HashLink>
        </motion.div>
    )
}
